// Detail view for one mini course. Editable title + description in
// the header; the body lists contained conversations (sorted by
// lecture) with a "new conversation" affordance.

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    getMiniCourse,
    patchMiniCourse,
    createConversation,
} from '../adminApi';
import { Loading, Err } from './Overview';

export default function MiniCourseDetail({ match, history }) {
    const id = match?.params?.id;
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [createOpen, setCreateOpen] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setData(null);
            setErr(null);
            try {
                const d = await getMiniCourse(id);
                if (!cancelled) setData(d);
            } catch (e) {
                if (!cancelled) setErr(e?.message || 'Failed to load');
            }
        })();
        return () => { cancelled = true; };
    }, [id, reloadKey]);

    if (err) return <Err msg={err} />;
    if (!data) return <Loading />;

    const mc = data.mini_course;
    const conversations = data.conversations || [];

    const onCreated = (conv) => {
        setCreateOpen(false);
        if (conv && conv.id) {
            history.push(`/admin/content/conversations/${conv.id}`);
        } else {
            setReloadKey((k) => k + 1);
        }
    };

    return (
        <div>
            <div style={styles.crumb}>
                <Link to="/admin/mini-courses" style={styles.crumbLink}>← Mini courses</Link>
            </div>
            <h2 style={styles.h2}>{mc.title || `Mini course #${mc.id}`}</h2>

            <MiniCourseHeaderForm mc={mc} onSaved={() => setReloadKey((k) => k + 1)} />

            <div style={styles.sectionRow}>
                <h3 style={styles.h3}>Conversations</h3>
                {!createOpen ? (
                    <button type="button" style={styles.btnPrimary} onClick={() => setCreateOpen(true)}>
                        + New conversation
                    </button>
                ) : null}
            </div>

            {createOpen ? (
                <NewConversationForm
                    miniCourseId={mc.id}
                    nextLecture={nextLecture(conversations)}
                    onCreated={onCreated}
                    onCancel={() => setCreateOpen(false)}
                />
            ) : null}

            <div style={styles.tableWrap}>
                <table style={styles.table}>
                    <thead>
                        <tr>
                            <Th align="right">Lecture</Th>
                            <Th>Title</Th>
                            <Th>Tags</Th>
                            <Th align="right">Lines</Th>
                            <Th align="right">Audio</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {conversations.length === 0 ? (
                            <tr>
                                <Td colSpan={5} style={{ textAlign: 'center', color: '#888' }}>
                                    No conversations yet. Add one above.
                                </Td>
                            </tr>
                        ) : conversations.map((c) => (
                            <tr key={c.id}>
                                <Td align="right">{c.lecture ?? '—'}</Td>
                                <Td>
                                    <Link to={`/admin/content/conversations/${c.id}`} style={styles.link}>
                                        {c.title || `#${c.id}`}
                                    </Link>
                                </Td>
                                <Td style={{ color: '#888', fontSize: 12 }}>{c.tags || '—'}</Td>
                                <Td align="right">{c.n_lines}</Td>
                                <Td align="right">
                                    <AudioBadge covered={c.n_lines_with_audio} total={c.n_lines} />
                                </Td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function nextLecture(conversations) {
    const taken = conversations.map((c) => c.lecture).filter((n) => n != null);
    if (taken.length === 0) return 1;
    return Math.max(...taken) + 1;
}

function MiniCourseHeaderForm({ mc, onSaved }) {
    const [title, setTitle] = useState(mc.title || '');
    const [description, setDescription] = useState(mc.description || '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [savedTick, setSavedTick] = useState(false);

    const dirty = (title !== (mc.title || '')) || (description !== (mc.description || ''));

    const onSave = async () => {
        if (!dirty || saving) return;
        try {
            setSaving(true);
            setError(null);
            await patchMiniCourse(mc.id, { title, description });
            setSavedTick(true);
            setTimeout(() => setSavedTick(false), 1400);
            onSaved && onSaved();
        } catch (e) {
            const msg = e?.response?.data?.error || e?.message || 'Save failed';
            setError(msg);
        } finally {
            setSaving(false);
        }
    };

    const onReset = () => {
        setTitle(mc.title || '');
        setDescription(mc.description || '');
        setError(null);
    };

    return (
        <div style={styles.headerForm}>
            <div style={styles.fieldRow}>
                <span style={styles.fieldLabel}>Title</span>
                <input
                    style={styles.input}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    disabled={saving}
                />
            </div>
            <div style={styles.fieldRow}>
                <span style={styles.fieldLabel}>Description</span>
                <textarea
                    style={styles.textarea}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={2}
                    disabled={saving}
                />
            </div>
            {error ? <div style={styles.err}>{error}</div> : null}
            {dirty || savedTick ? (
                <div style={styles.fieldRow}>
                    {dirty ? (
                        <>
                            <button type="button" style={styles.btnLink} onClick={onReset} disabled={saving}>
                                Reset
                            </button>
                            <button type="button" style={styles.btnPrimary} onClick={onSave} disabled={saving}>
                                {saving ? 'Saving…' : 'Save'}
                            </button>
                        </>
                    ) : null}
                    {savedTick ? <span style={styles.saved}>Saved ✓</span> : null}
                </div>
            ) : null}
        </div>
    );
}

function NewConversationForm({ miniCourseId, nextLecture, onCreated, onCancel }) {
    const [title, setTitle] = useState('');
    const [lecture, setLecture] = useState(String(nextLecture));
    const [tags, setTags] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!title.trim()) {
            setError('Title is required');
            return;
        }
        try {
            setSaving(true);
            setError(null);
            const res = await createConversation({
                title: title.trim(),
                mini_course_id: miniCourseId,
                lecture: lecture === '' ? null : parseInt(lecture, 10),
                tags: tags.trim() || null,
            });
            onCreated && onCreated(res?.conversation || res);
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || 'Create failed';
            setError(msg);
        } finally {
            setSaving(false);
        }
    };

    return (
        <form onSubmit={onSubmit} style={styles.newForm}>
            <div style={styles.newFormRow}>
                <input
                    style={styles.input}
                    placeholder="Title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    disabled={saving}
                    autoFocus
                />
                <input
                    type="number"
                    style={styles.numInput}
                    placeholder="Lecture"
                    value={lecture}
                    onChange={(e) => setLecture(e.target.value)}
                    disabled={saving}
                    min={1}
                />
            </div>
            <div style={styles.newFormRow}>
                <input
                    style={styles.input}
                    placeholder="Tags (optional)"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    disabled={saving}
                />
            </div>
            {error ? <div style={styles.err}>{error}</div> : null}
            <div style={styles.newFormRow}>
                <button type="submit" style={styles.btnPrimary} disabled={saving}>
                    {saving ? 'Creating…' : 'Create & open editor'}
                </button>
                <button
                    type="button"
                    style={styles.btnLink}
                    onClick={() => { setError(null); onCancel && onCancel(); }}
                    disabled={saving}
                >
                    Cancel
                </button>
            </div>
        </form>
    );
}

function AudioBadge({ covered, total }) {
    if (total === 0) return <span style={{ color: '#bbb' }}>—</span>;
    const full = covered >= total;
    const empty = covered === 0;
    const color = full ? '#2e7d32' : empty ? '#c0392b' : '#ed8936';
    return (
        <span style={{ color, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {covered}/{total}
        </span>
    );
}

function Th({ children, align }) {
    return <th style={{ ...styles.th, textAlign: align || 'left' }}>{children}</th>;
}
function Td({ children, align, style, colSpan }) {
    return <td colSpan={colSpan} style={{ ...styles.td, textAlign: align || 'left', ...(style || {}) }}>{children}</td>;
}

const styles = {
    crumb: { marginBottom: 8 },
    crumbLink: { color: '#888', textDecoration: 'none', fontSize: 13 },
    h2: { marginTop: 0, color: '#222' },
    h3: { margin: 0, color: '#222', fontSize: 15 },
    sectionRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 18, marginBottom: 10 },
    headerForm: { padding: '12px 16px', backgroundColor: '#fff', borderRadius: 12, marginBottom: 12, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' },
    fieldRow: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 },
    fieldLabel: { width: 90, fontSize: 11, color: '#888', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 },
    input: { flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 14 },
    numInput: { width: 110, padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 14 },
    textarea: { flex: 1, padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', fontSize: 14, fontFamily: 'inherit', resize: 'vertical' },
    tableWrap: { backgroundColor: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    th: { padding: '10px 12px', borderBottom: '1px solid #eee', color: '#888', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 },
    td: { padding: '10px 12px', borderBottom: '1px solid #f3f3f3', color: '#222' },
    link: { color: '#d97447', textDecoration: 'none', fontWeight: 600 },
    btnPrimary: { padding: '8px 14px', borderRadius: 6, border: '1px solid #d97447', background: '#d97447', color: '#fff', fontWeight: 600, cursor: 'pointer' },
    btnLink: { padding: '8px 0', border: 'none', background: 'transparent', color: '#888', cursor: 'pointer', marginRight: 8 },
    newForm: { padding: 14, backgroundColor: '#fff', borderRadius: 12, marginBottom: 12, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' },
    newFormRow: { display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' },
    err: { color: '#c0392b', fontSize: 12, marginBottom: 8 },
    saved: { color: '#2e7d32', fontSize: 12, fontWeight: 600 },
};
